/**
 * Admin venue editor: the VL dashboard iframes `/steps/*?embed=admin` and posts its own
 * admin session + target venue id. API calls then send `X-Venue-Id`, which the API's
 * `admin.as-venue` middleware honours for admins only.
 *
 * The session lives in memory only, so it never touches this site's own login/storage.
 */
export interface AdminEmbedSession {
  token: string;
  /** Exactly one is set: venue editor (/steps) or member editor (/member). */
  venueId?: string;
  memberId?: string;
}

export const isAdminEmbed =
  typeof window !== "undefined" &&
  window.parent !== window &&
  new URLSearchParams(window.location.search).get("embed") === "admin";

let session: AdminEmbedSession | null = null;
const listeners = new Set<(s: AdminEmbedSession) => void>();

export const getAdminEmbedSession = (): AdminEmbedSession | null => session;

export function onAdminEmbedSession(cb: (s: AdminEmbedSession) => void): () => void {
  if (session) cb(session);
  listeners.add(cb);
  return () => listeners.delete(cb);
}

// Comma-separated dashboard origins allowed to hand us a session. Unset = embed disabled (fail closed).
const DASHBOARD_ORIGINS = String(import.meta.env.VITE_ADMIN_DASHBOARD_ORIGINS ?? "")
  .split(",")
  .map((o) => o.trim().replace(/\/$/, ""))
  .filter(Boolean);

if (isAdminEmbed) {
  window.addEventListener("message", (event) => {
    if (event.source !== window.parent || !DASHBOARD_ORIGINS.includes(event.origin)) return;
    const data = event.data as { type?: string; token?: unknown; venueId?: unknown; memberId?: unknown } | null;
    if (data?.type !== "vl-admin-embed-session") return;
    if (typeof data.token !== "string") return;
    if (typeof data.venueId === "string") session = { token: data.token, venueId: data.venueId };
    else if (typeof data.memberId === "string") session = { token: data.token, memberId: data.memberId };
    else return;
    listeners.forEach((cb) => cb(session!));
  });
  // postMessage takes one target origin; a non-matching one is silently dropped by the browser.
  DASHBOARD_ORIGINS.forEach((origin) => window.parent.postMessage({ type: "vl-admin-embed-ready" }, origin));
}
